import { cycleGroupDpsk, deleteDevices } from 'features/tenant/tenantServices';
import { createTenantUnitReference } from 'shared/api/tenants';
import { cancelSubscription, updateSubscription } from 'shared/api/subscription';
import { resetTenantVlansByUnit } from 'shared/api/tenant-radius';
import { deleteOnboardCredentials } from 'shared/api/auth';
import { deleteOnboardAccount } from 'features/tenant/api/onboard';

// Helper function for useTenantMoveState.
export function processDevices(devices, ethernetEnabled) {
  const result = {
    deleteList: [],
    wiredList: [],
  };

  devices.forEach((d) => {
    // Wired devices don't need changes unless the new building doesn't have ethernet.
    if (!ethernetEnabled) {
      result.deleteList.push(d);
    } else {
      result.wiredList.push(d);
    }
  });
  return result;
}

// Makes API calls to move the tenant.
//
// 1: Cycle new unit group dpsk
// 2: Cancel subscription
// 3: Delete wireless devices
// 4: Update unit reference
// 5: Upload wireless devices
// 6: Cycle old unit group dpsk
// 7: Update subscription
// 8: Update radius records
// 9: Remove Onboard account and Auth credentials if new building does not require an account
export async function moveTenant(request, tenantMoveState, user, cancelSub, newPlanId) {
  const {
    deviceActions,
    hasSubscription,
    isSameUnit,
    newBuilding,
    newUnit,
    tenant,
  } = tenantMoveState;

  const {
    id: tenantId,
    onboard_account: onboardAccount,
  } = tenant;
  const {
    deleteList,
    wiredList,
  } = deviceActions;

  const status = {
    errors: [],
    moved: false,
  };
  let result;

  if (!isSameUnit) {
    result = await cycleGroupDpsk(request, newBuilding, newUnit);
    if (!result) {
      status.errors = ['failed to cycle group DPSK in new unit'];
      return status;
    }
  }

  if (cancelSub) {
    result = await request(cancelSubscription(
      tenantId,
      true,
      `reactor: canceled service during move to unit ${newUnit.id}`,
      user.username, // This is the logged-in user, not the tenant username.
    ));
    if (!result) {
      status.errors = ['failed to cancel subscription'];
      return status;
    }
  }

  const numDeleted = await deleteDevices(request, deleteList);
  if (numDeleted !== deleteList.length) {
    status.errors = ['failed to delete devices'];
    return status;
  }

  if (!isSameUnit) {
    result = await request(createTenantUnitReference(tenantId, newUnit.id, user.username));
    if (!result) {
      status.errors = ['failed to create tenant unit reference'];
      return status;
    }
  }

  // The tenant's unit reference is updated.
  status.moved = true;

  const {
    currentBuilding: oldBuilding,
    currentUnit: oldUnit,
  } = tenantMoveState;

  if (!isSameUnit) {
    result = await cycleGroupDpsk(request, oldBuilding, oldUnit);
    if (!result) {
      status.errors.push('failed to cycle group DPSK in old unit');
    }
  }

  if (hasSubscription && !cancelSub) {
    // The newPlanId parameter might not be defined.
    // In this case the route will just ensure the subscription's metadata is correct.
    result = await request(updateSubscription(tenantId, newPlanId));
    if (!result) {
      status.errors.push('failed to update subscription');
    }
  }

  if (wiredList.length > 0) {
    result = await request(resetTenantVlansByUnit(newUnit.id));
    if (!result) {
      status.errors.push('failed to reset radius vlans');
    }
  }
  if (onboardAccount?.id && !newBuilding.auth_method.includes('802.1x')) {
    result = await request(deleteOnboardAccount(onboardAccount.id), 'Removed Onboard account')
      .then(async (deleteResult) => {
        if (deleteResult) {
          // DB cascade should delete all creds, but call to ensure cleanup
          return request(deleteOnboardCredentials(onboardAccount.id), 'Removed Onboard credentials');
        }
        status.errors.push('failed to delete onboard credentials');
        return deleteResult;
      });
    if (!result) {
      status.errors.push('failed to delete onboard account');
    }
  }
  return status;
}
